/**
 * Fetch blog posts mentioning a city (used by city/category pages).
 *
 * Strategy: text search on city name. Most travel posts surface the city in
 * title or excerpt, so this catches "Things to do in Marrakech", "5 days in
 * Marrakech", etc. without backend changes. Returns null while loading or on
 * error so the consumer can render nothing rather than a skeleton.
 */
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { parseLang } from "@majourneys/shared";
import type { Lang } from "@majourneys/shared";
import { blogApi } from "../api/blog.api";
import { blogKeys } from "../constants/query-keys";

/**
 * Shared between the hook below and route loaders. Prefetching this in a
 * loader is what puts the "Read about {city}" article anchors into the
 * server-rendered HTML — without it the section only appears after client
 * hydration, so crawlers never see a city hub link to its own articles
 * (which is exactly why /place/tetouan outranked the Tetouan article).
 */
export function cityArticlesQueryOptions(
  lang: Lang,
  cityName: string | null | undefined,
  limit = 3,
) {
  return queryOptions({
    queryKey: [...blogKeys.all, "city", lang, cityName ?? "", limit] as const,
    queryFn: async () => {
      if (!cityName) return [];
      const res = await blogApi.list(lang, { search: cityName, page: 1 });
      return res.data.items.slice(0, limit);
    },
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

export function useCityArticles(cityName: string | null | undefined, limit = 3) {
  const { lang } = useParams({ strict: false }) as { lang?: string };
  const l = parseLang(lang);
  const query = useQuery({
    ...cityArticlesQueryOptions(l, cityName, limit),
    enabled: !!cityName,
  });
  return query.data ?? null;
}
